<template>
  <div class="espiral-app-container" :style="themeStyle">
    <div class="espiral-shell">
      <!-- Header -->
      <div class="espiral-header">
        <div class="espiral-header-icon">
          <i class="fas fa-wallet"></i>
        </div>
        <div>
          <h2>{{ translate('Billetera') }}</h2>
          <p>{{ translate('Deposita o solicita un retiro de forma segura.') }}</p>
        </div>
      </div>

      <section v-if="!activeSection" class="espiral-operation-picker" aria-labelledby="espiral-operation-title">
        <p class="espiral-operation-eyebrow">{{ translate('Operaciones de billetera') }}</p>
        <h3 id="espiral-operation-title">{{ translate('¿Qué deseas hacer?') }}</h3>
        <p class="espiral-operation-description">{{ translate('Selecciona la operación que deseas realizar.') }}</p>
        <div class="espiral-operation-grid">
          <button type="button" class="espiral-operation-card espiral-operation-card--deposit" @click="selectSection('deposit')">
            <span class="espiral-operation-icon"><i class="fas fa-arrow-down"></i></span>
            <span>
              <strong>{{ translate('Ingresar dinero') }}</strong>
              <small>{{ translate('Deposita fondos en tu cuenta') }}</small>
            </span>
            <i class="fas fa-chevron-right espiral-operation-arrow" aria-hidden="true"></i>
          </button>
          <button type="button" class="espiral-operation-card espiral-operation-card--withdraw" @click="selectSection('withdraw')">
            <span class="espiral-operation-icon"><i class="fas fa-arrow-up"></i></span>
            <span>
              <strong>{{ translate('Retirar fondos') }}</strong>
              <small>{{ translate('Envía dinero a tu banco') }}</small>
            </span>
            <i class="fas fa-chevron-right espiral-operation-arrow" aria-hidden="true"></i>
          </button>
        </div>
      </section>

      <button v-else-if="activeSection && !withdrawalSuccess" type="button" class="espiral-back" :style="skin.my_account.close_btn" @click="backToWalletHome">
        <i class="fas fa-arrow-left"></i> {{ translate('Volver a Billetera') }}
      </button>

      <div v-if="activeSection === 'deposit' && !loadingWallets && !errorWallets && paymentMethods.length" class="espiral-progress">
        <div :class="{ active: step >= 1 }"><span>1</span>{{ translate('Método') }}</div>
        <i></i>
        <div :class="{ active: step >= 2 }"><span>2</span>{{ translate('Datos') }}</div>
      </div>

      <!-- Loading: consultando wallets -->
      <div v-if="activeSection === 'deposit' && loadingWallets" class="espiral-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>{{ translate('Cargando métodos de pago…') }}</p>
      </div>

      <!-- Error consultando wallets (CA15) -->
      <div v-else-if="activeSection === 'deposit' && errorWallets" class="espiral-state">
        <p class="espiral-error">{{ translate('No fue posible cargar las pasarelas de pago.') }}</p>
        <button class="espiral-btn" @click="fetchWallets">
          {{ translate('Reintentar') }}
        </button>
      </div>

      <!-- Sin wallets activas (CA14) -->
      <div v-else-if="activeSection === 'deposit' && paymentMethods.length === 0" class="espiral-state">
        <p>{{ translate('No hay métodos de depósito disponibles.') }}</p>
      </div>

      <!-- STEP 1 — Métodos de pago activos -->
      <div v-else-if="activeSection === 'deposit' && step === 1" class="espiral-step">
        <h4 class="espiral-step-title">
          {{ translate('Elige cómo depositar') }}
        </h4>
        <div class="espiral-methods-grid" :class="{ 'espiral-methods-grid--single': paymentMethods.length === 1 }">
          <div v-for="option in paymentMethods" :key="`${option.wallet.id}-${option.method.id}`"
            class="espiral-method-card" @click="selectMethod(option.wallet, option.method)">
            <div class="espiral-method-logo">
              <i class="fas fa-university"></i>
            </div>
            <span class="espiral-method-name">
              {{ displayPaymentName(option.method.name) }}
            </span>
            <span class="espiral-method-status">
              {{ translate('Disponible') }}
              <i :style="{ backgroundColor: option.method.rgb || 'transparent' }"></i>
            </span>
          </div>
        </div>
      </div>

      <!-- STEP 2 — Formulario de depósito -->
      <div v-else-if="activeSection === 'deposit' && step === 2" class="espiral-step espiral-form-wrap">
        <button class="espiral-back" :style="skin.my_account.close_btn" @click="backToMethods">
          &larr; {{ translate('Volver') }}
        </button>
        <h4 class="espiral-step-title">
          {{ translate('Datos de depósito') }}
        </h4>

        <form @submit.prevent="submitPayment" class="espiral-form">
          <div class="espiral-selected-method">
            <i class="fas fa-shield-alt"></i>
            <span>{{ displayPaymentName(selectedMethod.name) }}</span>
            <strong>{{ currency }}</strong>
          </div>
          <div class="espiral-field">
            <label>{{ translate('Nombre') }}</label>
            <input v-model="form.name" type="text" class="espiral-input" :style="skin.my_account.search" required
              :placeholder="translate('Ingresa tu nombre completo')" autocomplete="name" />
          </div>

          <div class="espiral-field">
            <label>{{ translate('Correo electrónico') }}</label>
            <input v-model="form.email" type="email" class="espiral-input" :style="skin.my_account.search" required
              :placeholder="translate('correo@ejemplo.com')" autocomplete="email" />
          </div>

          <div class="espiral-field">
            <label>{{ translate('Teléfono') }}</label>
            <input v-model="form.phone" type="text" class="espiral-input" :style="skin.my_account.search" required
              :placeholder="translate('Ingresa tu número de teléfono')" autocomplete="tel" />
          </div>

          <div class="espiral-field">
            <label>{{ translate('Monto') }}</label>
            <input v-model.number="form.amount" type="number" class="espiral-input" :style="skin.my_account.search"
              :min="minimumAmount" step="0.01" required
              :placeholder="`${translate('Monto mínimo')}: ${formatAmount(minimumAmount)} ${minimumCurrency}`" autocomplete="off" />
            <small class="espiral-minimum-amount">
              {{ translate('Monto mínimo') }}: {{ formatAmount(minimumAmount) }} {{ minimumCurrency }}
            </small>
          </div>

          <input type="hidden" v-model="currency" />

          <div v-if="formErrorMessage" class="espiral-alert espiral-alert-error">
            {{ formErrorMessage }}
          </div>

          <button type="submit" class="espiral-btn espiral-btn-submit" :disabled="submitting">
            <span v-if="submitting">
              <i class="fas fa-spinner fa-spin"></i> {{ translate('Procesando…') }}
            </span>
            <span v-else>{{ translate('Pagar') }}</span>
          </button>
        </form>
      </div>

      <div v-else-if="activeSection === 'withdraw'" class="espiral-step espiral-form-wrap">
        <section v-if="withdrawalSuccess" class="espiral-withdrawal-complete" role="status">
          <div class="espiral-withdrawal-complete-icon"><i class="fas fa-check"></i></div>
          <h4>{{ translate('Solicitud enviada') }}</h4>
          <p>{{ withdrawalSuccess }}</p>
          <small>{{ translate('Un agente revisará y procesará tu retiro.') }}</small>
          <button type="button" class="espiral-btn espiral-btn-submit" @click="goToLobby">
            <i class="fas fa-home"></i> {{ translate('Ir al lobby') }}
          </button>
        </section>
        <template v-else>
          <h4 class="espiral-step-title">{{ translate('Solicitud de retiro') }}</h4>
          <form @submit.prevent="submitWithdrawal" class="espiral-form">
            <div class="espiral-selected-method">
              <i class="fas fa-wallet"></i>
              <span>{{ translate('Saldo disponible') }}</span>
              <strong>{{ formatAmount(balance) }} {{ currency }}</strong>
            </div>

            <div class="espiral-field">
              <label for="withdrawal-name">{{ translate('Nombre completo') }}</label>
              <input id="withdrawal-name" v-model.trim="withdrawalForm.nombre" type="text" class="espiral-input"
                :style="skin.my_account.search" required autocomplete="name"
                :placeholder="translate('Ingresa el nombre del titular')" />
            </div>

            <div class="espiral-field">
              <label for="withdrawal-account">{{ translate('Cuenta o clave bancaria') }}</label>
              <input id="withdrawal-account" v-model.trim="withdrawalForm.bankAccount" type="text" class="espiral-input"
                :style="skin.my_account.search" required inputmode="numeric" autocomplete="off"
                :placeholder="translate('Ingresa tu cuenta o CLABE')" />
            </div>

            <div class="espiral-field">
              <label for="withdrawal-phone">{{ translate('Número de contacto') }}</label>
              <input id="withdrawal-phone" v-model.trim="withdrawalForm.contactNumber" type="tel" class="espiral-input"
                :style="skin.my_account.search" required inputmode="numeric" autocomplete="tel"
                :placeholder="translate('Ingresa tu número de contacto')" />
            </div>

            <div class="espiral-field">
              <label for="withdrawal-amount">{{ translate('Monto a retirar') }}</label>
              <input id="withdrawal-amount" v-model.number="withdrawalForm.capital" type="number" class="espiral-input"
                :style="skin.my_account.search" min="0.01" step="0.01" required autocomplete="off"
                :placeholder="translate('Ingresa el monto a retirar')" />
            </div>

            <div v-if="withdrawalError" class="espiral-alert espiral-alert-error">{{ withdrawalError }}</div>

            <button type="submit" class="espiral-btn espiral-btn-submit" :disabled="withdrawing">
              <span v-if="withdrawing"><i class="fas fa-spinner fa-spin"></i> {{ translate('Procesando…') }}</span>
              <span v-else>{{ translate('Solicitar retiro') }}</span>
            </button>
          </form>
        </template>
      </div>
    </div>

    <div v-if="paymentInstructions" class="espiral-instructions-overlay" role="dialog" aria-modal="true"
      aria-labelledby="espiral-instructions-title">
      <section class="espiral-instructions-modal">
        <div class="espiral-instructions-icon">
          <i class="fas fa-university"></i>
        </div>
        <h2 id="espiral-instructions-title">{{ translate('Datos para tu transferencia') }}</h2>
        <p class="espiral-instructions-intro">
          {{ translate('Realiza la transferencia desde la aplicación de tu banco con los siguientes datos.') }}
        </p>

        <section class="espiral-spei-guide">
          <h3>{{ translate('Instrucciones para realizar tu pago con SPEI') }}</h3>
          <ol>
            <li>{{ translate('Ingresa a la banca en línea desde la aplicación de tu banco.') }}</li>
            <li>{{ translate('Selecciona Transferencias y pagos.') }}</li>
            <li>
              {{ translate('Registra los siguientes datos para realizar tu depósito SPEI:') }}
              <ul>
                <li>{{ translate('Tipo de cuenta') }}: <strong>{{ bankTransfer.account_type }}</strong></li>
                <li>{{ translate('Banco') }}: <strong> Transfer </strong></li>
                <li>{{ translate('Beneficiario') }}: <strong> Productora Zhylo </strong> </li>
              </ul>
            </li>
            <li>{{ translate('Realiza tu transferencia SPEI; el dinero se acreditará a tu saldo.') }}</li>
          </ol>
          <p class="espiral-spei-note">
            {{ translate('Nota: El monto mínimo a depositar es') }} <strong>{{ formatAmount(minimumAmount) }} {{ minimumCurrency }}</strong>.
          </p>
        </section>

        <div class="espiral-instruction-value">
          <span>{{ translate('Concepto de pago') }}</span>
          <div>
            <strong>{{ paymentInstructions.concept }}</strong>
            <button type="button" @click="copyToClipboard(paymentInstructions.concept, 'concept')">
              <i :class="copiedField === 'concept' ? 'fas fa-check' : 'far fa-copy'"></i>
              {{ copiedField === 'concept' ? translate('Copiado') : translate('Copiar') }}
            </button>
          </div>
        </div>

        <div class="espiral-instruction-value">
          <span>{{ translate('CLABE interbancaria') }}</span>
          <div>
            <strong>{{ paymentInstructions.interbankClabe }}</strong>
            <button type="button" @click="copyToClipboard(paymentInstructions.interbankClabe, 'interbankClabe')">
              <i :class="copiedField === 'interbankClabe' ? 'fas fa-check' : 'far fa-copy'"></i>
              {{ copiedField === 'interbankClabe' ? translate('Copiado') : translate('Copiar') }}
            </button>
          </div>
        </div>

        <div class="espiral-instructions-warning">
          <i class="fas fa-exclamation-triangle"></i>
          <p>
            {{ translate('Importante: Para completar tu pago, ingresa esta CLABE interbancaria y este concepto de pago
            en la aplicación de tu banco al momento de realizar la transferencia.') }}
          </p>
          <p>
            {{ translate('Verifica cuidadosamente la información. No agregues, elimines ni modifiques ningún carácter
            del concepto o de la CLABE.') }}
          </p>
        </div>

        <button type="button" class="espiral-btn espiral-instructions-close" @click="closePaymentInstructions">
          {{ translate('Entendido') }}
        </button>
      </section>
    </div>

    <div v-if="copyToastMessage" class="espiral-copy-toast" role="status" aria-live="polite">
      <i class="fas fa-check-circle"></i>
      {{ copyToastMessage }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const PAYMENTS_API = (process.env.VUE_APP_PAYMENTS_API_URL || '').replace(/\/$/, '');

export default {
  name: 'EspiralApp',
  props: {
    skin: { type: Object, required: true },
    language: { type: String, default: 'es' },
    user: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      step: 1,
      activeSection: null,
      wallets: [],
      clientId: null,
      selectedWallet: null,
      selectedMethod: null,
      loadingWallets: false,
      errorWallets: null,
      submitting: false,
      formErrorMessage: '',
      paymentInstructions: null,
      copiedField: '',
      copyToastMessage: '',
      copyToastTimer: null,
      form: {
        name: '',
        email: '',
        phone: '',
        amount: null
      },
      withdrawalForm: {
        nombre: '',
        bankAccount: '',
        contactNumber: '',
        capital: null
      },
      withdrawing: false,
      withdrawalError: '',
      withdrawalSuccess: ''
    };
  },
  computed: {
    paymentLabel() {
      return 'SPEI y Tiendas de Conveniencia';
    },
    config() {
      return this.skin?.espiral_app_config || {};
    },
    theme() {
      return this.config.theme || {};
    },
    layout() {
      return this.config.layout || {};
    },
    bankTransfer() {
      return this.config.bank_transfer || {};
    },
    minimumAmount() {
      const amount = Number(this.bankTransfer.minimum_amount);
      return Number.isFinite(amount) && amount > 0 ? amount : 0.01;
    },
    minimumCurrency() {
      return this.bankTransfer.minimum_currency || this.currency;
    },
    themeStyle() {
      return {
        '--espiral-page-background': this.theme.page_background,
        '--espiral-header-background': this.theme.header_background,
        '--espiral-surface-background': this.theme.surface_background,
        '--espiral-modal-background': this.theme.modal_background,
        '--espiral-input-background': this.theme.input_background,
        '--espiral-accent': this.theme.accent,
        '--espiral-accent-dark': this.theme.accent_dark,
        '--espiral-accent-text': this.theme.accent_text,
        '--espiral-text': this.theme.text,
        '--espiral-muted-text': this.theme.muted_text,
        '--espiral-warning': this.theme.warning,
        '--espiral-warning-text': this.theme.warning_text,
        '--espiral-error-text': this.theme.error_text,
        '--espiral-font-family': this.layout.font_family,
        '--espiral-page-padding': this.layout.page_padding,
        '--espiral-header-padding': this.layout.header_padding,
        '--espiral-header-radius': this.layout.header_radius,
        '--espiral-grid-gap': this.layout.grid_gap,
        '--espiral-card-padding': this.layout.card_padding,
        '--espiral-card-radius': this.layout.card_radius,
        '--espiral-form-padding': this.layout.form_padding,
        '--espiral-form-radius': this.layout.form_radius,
        '--espiral-input-padding': this.layout.input_padding,
        '--espiral-input-radius': this.layout.input_radius,
        '--espiral-button-min-height': this.layout.button_min_height,
        '--espiral-button-radius': this.layout.button_radius,
        '--espiral-modal-padding': this.layout.modal_padding,
        '--espiral-modal-radius': this.layout.modal_radius
      };
    },
    currency() {
      return this.user?.user_data?.data?.currency || '';
    },
    balance() {
      const amount = Number(this.user?.user_data?.data?.balance);
      return Number.isFinite(amount) ? amount : 0;
    },
    apiUrlnew() {
      return this.$store.getters['getApiUrlnew'] || '';
    },
    userToken() {
      return (
        this.user?.token
        || this.user?.user_data?.data?.token
        || this.user?.user_data?.token
        || ''
      );
    },
    paymentMethods() {
      return this.wallets.reduce((methods, wallet) => {
        return methods.concat(
          this.activeMethodsOf(wallet).map(method => ({ wallet, method }))
        );
      }, []);
    }
  },
  mounted() {
    this.fetchWallets();
  },
  beforeDestroy() {
    clearTimeout(this.copyToastTimer);
  },
  methods: {
    displayPaymentName(name) {
      return /espir|esperi/i.test(name || '') ? this.paymentLabel : name;
    },
    selectSection(section) {
      this.activeSection = section;
      this.withdrawalError = '';
      this.withdrawalSuccess = '';
      this.formErrorMessage = '';
    },
    backToWalletHome() {
      this.activeSection = null;
      this.withdrawalError = '';
      this.withdrawalSuccess = '';
      this.formErrorMessage = '';
    },
    formatAmount(amount) {
      return new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount);
    },
    translate(text) {
      if (!text || !this.skin?.translation) return text;
      const dict = this.skin.translation[text] || this.skin.translation[text.replace(/ /g, '_')];
      return dict ? dict[this.language] || dict.en || text : text;
    },
    activeMethodsOf(wallet) {
      if (!wallet) return [];
      // Nota: el backend usa "paymetsMethods" (con typo). Se respeta mientras se corrige en backend.
      return (wallet.paymetsMethods || []).filter(m => m.status === true);
    },
    async fetchWallets() {
      this.loadingWallets = true;
      this.errorWallets = null;
      this.clientId = null;
      if (!PAYMENTS_API) {
        this.errorWallets = new Error('La URL de pagos no está configurada.');
        this.loadingWallets = false;
        return;
      }
      if (!this.userToken) {
        this.errorWallets = new Error('Sin sesión de usuario');
        this.loadingWallets = false;
        return;
      }
      try {
        const res = await axios.post(`${PAYMENTS_API}/wallets/by-plataform`, {
          plataformId: this.config.plataform_id,
          token: this.userToken
        });
        const all = res.data?.data?.wallets || [];
        this.clientId = res.data?.data?.user?.id || null;
        this.wallets = all.filter(w => w.status === true);
      } catch (err) {
        this.errorWallets = err;
      } finally {
        this.loadingWallets = false;
      }
    },
    selectMethod(wallet, method) {
      this.selectedWallet = wallet;
      this.selectedMethod = method;
      this.formErrorMessage = '';
      this.step = 2;
    },
    backToMethods() {
      this.selectedWallet = null;
      this.selectedMethod = null;
      this.formErrorMessage = '';
      this.step = 1;
    },
    validateForm() {
      if (!this.selectedWallet || !this.selectedWallet.id) {
        this.formErrorMessage = this.translate('Seleccione una pasarela.');
        return false;
      }
      if (!this.selectedMethod || !this.selectedMethod.id) {
        this.formErrorMessage = this.translate('Seleccione un método de pago.');
        return false;
      }
      if (!this.form.name || !this.form.name.trim()) {
        this.formErrorMessage = this.translate('El nombre es obligatorio.');
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.form.email || '')) {
        this.formErrorMessage = this.translate('El correo no es válido.');
        return false;
      }
      if (!this.form.phone || !this.form.phone.trim()) {
        this.formErrorMessage = this.translate('El teléfono es obligatorio.');
        return false;
      }
      if (typeof this.form.amount !== 'number' || isNaN(this.form.amount) || this.form.amount < this.minimumAmount) {
        this.formErrorMessage = `${this.translate('El monto mínimo es')} ${this.formatAmount(this.minimumAmount)} ${this.minimumCurrency}.`;
        return false;
      }
      if (!this.currency) {
        this.formErrorMessage = this.translate('No hay moneda configurada.');
        return false;
      }
      if (!this.userToken) {
        this.formErrorMessage = this.translate('Su sesión ha expirado. Inicie sesión nuevamente.');
        return false;
      }
      if (!this.clientId) {
        this.formErrorMessage = this.translate('No fue posible identificar al usuario para el pago.');
        return false;
      }
      this.formErrorMessage = '';
      return true;
    },
    buildPayload() {
      return {
        internal: {
          type: 'payment',
          client_id: this.clientId,
          wallet_id: this.selectedWallet.id,
          token: this.userToken
        },
        esperilapp: {
          name: this.form.name,
          email: this.form.email,
          phone: this.form.phone,
          amount: Number(this.form.amount),
          currency: this.currency
        }
      };
    },
    showPaymentInstructions(response) {
      const instructions = response?.data?.data || response?.data || {};

      if (instructions.concept == null || instructions.interbankClabe == null) {
        return false;
      }

      this.paymentInstructions = {
        concept: instructions.concept,
        interbankClabe: instructions.interbankClabe
      };
      this.copiedField = '';
      return true;
    },
    async copyToClipboard(value, field) {
      const text = String(value);

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          this.copyUsingTextArea(text);
        }
      } catch {
        try {
          this.copyUsingTextArea(text);
        } catch {
          this.formErrorMessage = this.translate('No fue posible copiar el dato. Inténtelo nuevamente.');
          return;
        }
      }

      this.copiedField = field;
      this.showCopyToast(field);
    },
    showCopyToast(field) {
      this.copyToastMessage = field === 'concept'
        ? this.translate('Concepto de pago copiado')
        : this.translate('CLABE interbancaria copiada');

      clearTimeout(this.copyToastTimer);
      this.copyToastTimer = setTimeout(() => {
        this.copyToastMessage = '';
      }, 3000);
    },
    copyUsingTextArea(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);

      try {
        textarea.select();
        if (!document.execCommand('copy')) {
          throw new Error('Copy command was rejected');
        }
      } finally {
        document.body.removeChild(textarea);
      }
    },
    closePaymentInstructions() {
      this.paymentInstructions = null;
      this.copiedField = '';
      this.copyToastMessage = '';
      clearTimeout(this.copyToastTimer);
      this.goToLobby();
    },
    goToLobby() {
      this.$store.dispatch('changeView', 'lobby');
    },
    async submitPayment() {
      if (this.submitting) return;
      if (!PAYMENTS_API) {
        this.formErrorMessage = this.translate('La URL de pagos no está configurada.');
        return;
      }
      if (!this.validateForm()) return;

      this.submitting = true;
      try {
        const res = await axios.post(`${PAYMENTS_API}/espiralapp/payment`, this.buildPayload());
        if (this.showPaymentInstructions(res)) {
          return;
        }
        this.formErrorMessage = this.translate('No se recibieron los datos bancarios para realizar la transferencia.');
      } catch (err) {
        const msg = err?.response?.data?.message
          || err?.message
          || this.translate('No fue posible crear la transacción.');
        //quiero extraer la parte del mensaje que viene despues del punto, o sea msg trae un punto y quiero lo que va despues del punto
        this.formErrorMessage = msg.split('.')[1];
      } finally {
        this.submitting = false;
      }
    },
    validateWithdrawal() {
      if (!this.withdrawalForm.nombre || !this.withdrawalForm.bankAccount || !this.withdrawalForm.contactNumber) {
        this.withdrawalError = this.translate('Completa todos los datos para solicitar el retiro.');
        return false;
      }
      if (!/^\d{7,15}$/.test(this.withdrawalForm.contactNumber)) {
        this.withdrawalError = this.translate('Ingresa un número de contacto válido.');
        return false;
      }
      const capital = Number(this.withdrawalForm.capital);
      if (!Number.isFinite(capital) || capital <= 0) {
        this.withdrawalError = this.translate('Ingresa un monto de retiro válido.');
        return false;
      }
      if (capital > this.balance) {
        this.withdrawalError = this.translate('No puedes retirar más de tu saldo disponible.');
        return false;
      }
      if (!this.userToken || !this.apiUrlnew) {
        this.withdrawalError = this.translate('Tu sesión ha expirado. Inicia sesión nuevamente.');
        return false;
      }
      return true;
    },
    async submitWithdrawal() {
      if (this.withdrawing) return;

      this.withdrawalError = '';
      this.withdrawalSuccess = '';
      if (!this.validateWithdrawal()) return;

      this.withdrawing = true;
      try {
        const response = await axios.post(`${this.apiUrlnew}transaction/withdrawal_requests`, {
          capital: Number(this.withdrawalForm.capital),
          bankAccount: this.withdrawalForm.bankAccount,
          nombre: this.withdrawalForm.nombre,
          contactNumber: this.withdrawalForm.contactNumber
        }, {
          headers: {
            'x-player-token': this.userToken,
            'x-api-key': 'FTBBW4bR1pKTUFsURbFD8RpcPygh8wN6'
          }
        });

        if (response.data?.error || response.data?.success === false) {
          throw new Error(response.data.error || response.data.message);
        }

        this.withdrawalForm = {
          nombre: '',
          bankAccount: '',
          contactNumber: '',
          capital: null
        };
        this.withdrawalSuccess = this.translate('Tu solicitud de retiro fue creada y está pendiente de revisión.');
      } catch (error) {
        this.withdrawalError = error?.response?.data?.message
          || error?.response?.data?.error
          || error?.message
          || this.translate('No fue posible crear la solicitud de retiro.');
      } finally {
        this.withdrawing = false;
      }
    }
  }
};
</script>

<style scoped>
.espiral-app-container {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: var(--espiral-page-padding, 28px 20px);
  box-sizing: border-box;
  color: var(--espiral-text, #fff);
  background: var(--espiral-page-background, linear-gradient(145deg, #000 0%, #151612 100%)) !important;
  font-family: var(--espiral-font-family, 'Roboto', Arial, sans-serif);
}

.espiral-shell {
  width: 100%;
  max-width: 850px;
  min-height: 100%;
  margin: 0 auto;
}

.espiral-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: var(--espiral-header-padding, 18px 20px);
  margin-bottom: 18px;
  border: 1px solid rgba(213, 218, 0, 0.34);
  border-radius: var(--espiral-header-radius, 14px);
  background: var(--espiral-header-background, linear-gradient(105deg, #12130c, #25280f));
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.32);
}

.espiral-header-icon,
.espiral-card-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: var(--espiral-accent-text, #111);
  background: var(--espiral-accent, #d5da00);
  box-shadow: 0 0 0 5px rgba(213, 218, 0, 0.1);
}

.espiral-header-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  font-size: 20px;
}

.espiral-header h2 {
  margin: 0 0 3px;
  color: var(--espiral-accent, #d5da00);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.espiral-header p {
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
}

.espiral-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  margin: 0 auto 24px;
  color: #73777f;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.espiral-progress div {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.espiral-progress span {
  display: grid;
  width: 21px;
  height: 21px;
  place-items: center;
  border: 1px solid #4f5359;
  border-radius: 50%;
  font-size: 10px;
}

.espiral-progress div.active {
  color: var(--espiral-accent, #d5da00);
}

.espiral-progress div.active span {
  color: var(--espiral-accent-text, #111);
  border-color: var(--espiral-accent, #d5da00);
  background: var(--espiral-accent, #d5da00);
}

.espiral-progress>i {
  width: 34px;
  height: 1px;
  background: #3a3c40;
}

.espiral-operation-picker {
  max-width: 720px;
  margin: 0 auto;
  padding: 12px 0;
}

.espiral-operation-eyebrow {
  margin: 0 0 6px;
  color: var(--espiral-muted-text, #a8abb0);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.espiral-operation-picker h3 {
  margin: 0;
  color: var(--espiral-text, #fff);
  font-size: 24px;
  font-weight: 800;
}

.espiral-operation-description {
  margin: 7px 0 18px;
  color: var(--espiral-muted-text, #d7d8d4);
  font-size: 13px;
}

.espiral-operation-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.espiral-operation-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 13px;
  min-height: 108px;
  padding: 18px;
  color: var(--espiral-text, #fff);
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--espiral-card-radius, 14px);
  background: var(--espiral-surface-background, linear-gradient(145deg, #24251e, #11120f));
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
  cursor: pointer;
  transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
}

.espiral-operation-card:hover,
.espiral-operation-card:focus-visible {
  border-color: var(--espiral-accent, #d5da00);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(213, 218, 0, 0.2);
  outline: 0;
  transform: translateY(-3px);
}

.espiral-operation-card strong,
.espiral-operation-card small {
  display: block;
}

.espiral-operation-card strong {
  font-size: 16px;
}

.espiral-operation-card small {
  margin-top: 5px;
  color: var(--espiral-muted-text, #d7d8d4);
  font-size: 12px;
}

.espiral-operation-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 18px;
}

.espiral-operation-card--deposit .espiral-operation-icon {
  color: #35d07f;
}

.espiral-operation-card--withdraw .espiral-operation-icon {
  color: #6b9cff;
}

.espiral-operation-arrow {
  color: var(--espiral-muted-text, #a8abb0);
  font-size: 13px;
}

.espiral-state {
  max-width: 480px;
  margin: 0 auto;
  text-align: center;
  padding: 48px 24px;
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--espiral-card-radius, 14px);
  background: var(--espiral-surface-background, rgba(17, 18, 20, 0.82));
}

.espiral-state .fa-spinner {
  font-size: 28px;
  margin-bottom: 12px;
}

.espiral-withdrawal-complete {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 460px;
  padding: 38px 26px;
  margin: 0 auto;
  text-align: center;
  border: 1px solid rgba(53, 208, 127, 0.35);
  border-radius: var(--espiral-card-radius, 14px);
  background: var(--espiral-surface-background, rgba(17, 18, 20, 0.82));
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
}

.espiral-withdrawal-complete-icon {
  display: grid;
  width: 58px;
  height: 58px;
  margin-bottom: 16px;
  place-items: center;
  color: #35d07f;
  border: 1px solid rgba(53, 208, 127, 0.5);
  border-radius: 50%;
  background: rgba(53, 208, 127, 0.12);
  font-size: 24px;
}

.espiral-withdrawal-complete h4 {
  margin: 0;
  color: var(--espiral-text, #fff);
  font-size: 21px;
}

.espiral-withdrawal-complete p {
  margin: 10px 0 6px;
  color: var(--espiral-text, #fff);
  font-size: 14px;
  line-height: 1.5;
}

.espiral-withdrawal-complete small {
  margin-bottom: 22px;
  color: var(--espiral-muted-text, #d7d8d4);
  font-size: 12px;
}

.espiral-withdrawal-complete .espiral-btn {
  width: 100%;
}

.espiral-error {
  color: #ff6b6b;
  margin-bottom: 12px;
}

.espiral-step {
  max-width: 720px;
  margin: 0 auto;
}

.espiral-step-title {
  text-align: center;
  margin-bottom: 20px;
  color: var(--espiral-accent, #d5da00);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.espiral-wallets-grid,
.espiral-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: var(--espiral-grid-gap, 14px);
}

.espiral-methods-grid--single {
  grid-template-columns: minmax(220px, 280px);
  justify-content: center;
}

.espiral-wallet-card,
.espiral-method-card {
  position: relative;
  min-height: 154px;
  padding: var(--espiral-card-padding, 18px);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: var(--espiral-card-radius, 14px);
  background: var(--espiral-surface-background, linear-gradient(145deg, #24251e, #11120f));
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
}

.espiral-wallet-card:hover,
.espiral-method-card:hover {
  border-color: var(--espiral-accent, #d5da00);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(213, 218, 0, 0.2);
  transform: translateY(-3px);
}

.espiral-card-icon {
  width: 32px;
  height: 32px;
  margin-bottom: 4px;
  border-radius: 9px;
  font-size: 14px;
}

.espiral-wallet-name {
  color: var(--espiral-text, #fff);
  font-weight: 700;
  font-size: 16px;
}

.espiral-wallet-description {
  margin: 0;
  color: #a8abb0;
  font-size: 12px;
}

.espiral-methods-preview {
  list-style: none;
  padding: 0;
  margin: 0;
  margin-top: auto;
  color: #ddd;
  font-size: 12px;
}

.espiral-methods-preview li {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.espiral-method-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.06);
}

.espiral-method-card {
  min-height: 138px;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.espiral-method-logo {
  display: grid;
  width: 76px;
  height: 64px;
  place-items: center;
  cursor: pointer;
  border: 1px solid rgba(213, 218, 0, 0.65);
  border-radius: 16px;
  background: linear-gradient(145deg, var(--espiral-accent, #d5da00), var(--espiral-accent-dark, #929900));
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.35);
  color: var(--espiral-accent-text, #111);
  font-size: 29px;
}

.espiral-method-name {
  color: var(--espiral-text, #fff);
  font-size: 14px;
  font-weight: 700;
}

.espiral-method-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--espiral-muted-text, #d7d8d4);
  font-size: 12px;
  font-weight: 600;
}

.espiral-method-status i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.08);
}

.espiral-back {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  color: var(--espiral-accent, #d5da00) !important;
  background: transparent;
  border: 1px solid rgba(213, 218, 0, 0.4);
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 12px;
  font-size: 14px;
  padding: 0 10px;
}

.espiral-form-wrap {
  max-width: 460px;
  margin: 0 auto;
}

.espiral-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: var(--espiral-form-padding, 22px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--espiral-form-radius, 14px);
  background: var(--espiral-surface-background, rgba(18, 19, 21, 0.92));
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
}

.espiral-selected-method {
  display: flex;
  align-items: center;
  gap: 9px;
  padding-bottom: 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: #d8dade;
  font-size: 12px;
}

.espiral-selected-method i {
  color: var(--espiral-accent, #d5da00);
}

.espiral-selected-method strong {
  margin-left: auto;
  color: var(--espiral-accent, #d5da00);
  font-size: 11px;
}

.espiral-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.espiral-field label {
  color: #d9dadd !important;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.espiral-input {
  width: 100%;
  padding: var(--espiral-input-padding, 11px 12px);
  color: var(--espiral-text, #fff) !important;
  border: 1px solid #34363b;
  border-radius: var(--espiral-input-radius, 7px);
  background: var(--espiral-input-background, #08090b) !important;
  outline: none;
  box-sizing: border-box;
  font-size: 14px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.espiral-input:focus {
  border-color: var(--espiral-accent, #d5da00);
  box-shadow: 0 0 0 3px rgba(213, 218, 0, 0.14);
}

.espiral-minimum-amount {
  color: var(--espiral-muted-text, #d7d8d4);
  font-size: 11px;
}

.espiral-btn {
  width: 100%;
  min-height: var(--espiral-button-min-height, 42px);
  padding: 10px 14px;
  color: var(--espiral-accent-text, #111);
  border: 1px solid var(--espiral-accent, #d5da00);
  border-radius: var(--espiral-button-radius, 7px);
  background: var(--espiral-accent, #d5da00);
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  transition: filter 0.18s ease, transform 0.18s ease;
}

.espiral-btn:hover:not(:disabled) {
  filter: brightness(1.2);
  transform: translateY(-1px);
}

.espiral-btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.espiral-alert {
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.espiral-alert-error {
  background: rgba(220, 53, 69, 0.2);
  color: var(--espiral-error-text, #ffb4ba);
  border: 1px solid rgba(220, 53, 69, 0.5);
}

.espiral-alert-success {
  border: 1px solid rgba(25, 135, 84, 0.55);
  color: #b9f6d1;
  background: rgba(25, 135, 84, 0.2);
}

.espiral-instructions-overlay {
  position: fixed;
  z-index: 10000;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(5px);
}

.espiral-instructions-modal {
  width: min(100%, 560px);
  max-height: 100%;
  overflow-y: auto;
  padding: var(--espiral-modal-padding, 28px);
  border: 1px solid rgba(213, 218, 0, 0.72);
  border-radius: var(--espiral-modal-radius, 16px);
  background: var(--espiral-modal-background, linear-gradient(145deg, #17180f, #080906));
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.56);
}

.espiral-instructions-icon {
  display: grid;
  width: 52px;
  height: 52px;
  margin: 0 auto 14px;
  place-items: center;
  border-radius: 50%;
  color: var(--espiral-accent-text, #111);
  background: var(--espiral-accent, #d5da00);
  box-shadow: 0 0 0 7px rgba(213, 218, 0, 0.12);
  font-size: 22px;
}

.espiral-instructions-modal h2 {
  margin: 0;
  color: var(--espiral-accent, #d5da00);
  font-size: 20px;
  font-weight: 800;
  text-align: center;
}

.espiral-instructions-intro {
  margin: 9px 0 22px;
  color: var(--espiral-muted-text, #d7d8d4);
  font-size: 14px;
  line-height: 1.45;
  text-align: center;
}

.espiral-spei-guide {
  margin: 0 0 18px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  color: var(--espiral-text, #fff);
  background: rgba(255, 255, 255, 0.05);
}

.espiral-spei-guide h3 {
  margin: 0 0 12px;
  color: var(--espiral-accent, #d5da00);
  font-size: 14px;
  font-weight: 800;
}

.espiral-spei-guide ol,
.espiral-spei-guide ul {
  margin: 0;
  padding-left: 20px;
}

.espiral-spei-guide li {
  margin-bottom: 7px;
  color: var(--espiral-muted-text, #d7d8d4);
  font-size: 13px;
  line-height: 1.4;
}

.espiral-spei-guide li:last-child {
  margin-bottom: 0;
}

.espiral-spei-guide ul {
  margin-top: 7px;
}

.espiral-spei-guide strong {
  color: var(--espiral-text, #fff);
}

.espiral-spei-note {
  margin: 14px 0 0;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--espiral-warning-text, #fff3c6);
  font-size: 13px;
  line-height: 1.4;
}

.espiral-spei-note strong {
  color: var(--espiral-warning, #ffd029);
}

.espiral-instruction-value {
  margin-bottom: 14px;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: var(--espiral-input-background, #050605);
}

.espiral-instruction-value>span {
  display: block;
  margin-bottom: 8px;
  color: var(--espiral-accent, #d5da00);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.espiral-instruction-value>div {
  display: flex;
  align-items: center;
  gap: 10px;
}

.espiral-instruction-value strong {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--espiral-text, #fff);
  font-family: monospace;
  font-size: clamp(18px, 5vw, 25px);
  letter-spacing: 0.04em;
}

.espiral-instruction-value button {
  flex: 0 0 auto;
  min-height: 34px;
  padding: 7px 10px;
  border: 1px solid rgba(213, 218, 0, 0.7);
  border-radius: 6px;
  color: var(--espiral-accent, #d5da00);
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

.espiral-instruction-value button:hover {
  color: var(--espiral-accent-text, #111);
  background: var(--espiral-accent, #d5da00);
}

.espiral-instructions-warning {
  margin: 20px 0;
  padding: 14px;
  border: 1px solid rgba(255, 193, 7, 0.55);
  border-radius: 10px;
  color: var(--espiral-warning-text, #fff3c6);
  background: rgba(255, 193, 7, 0.1);
}

.espiral-instructions-warning>i {
  display: block;
  margin-bottom: 8px;
  color: var(--espiral-warning, #ffd029);
  font-size: 18px;
}

.espiral-instructions-warning p {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
}

.espiral-instructions-warning p+p {
  margin-top: 10px;
  font-weight: 700;
}

.espiral-instructions-close {
  margin-top: 2px;
}

.espiral-copy-toast {
  position: fixed;
  z-index: 10001;
  bottom: 24px;
  left: 50%;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 16px;
  border: 1px solid rgba(213, 218, 0, 0.75);
  border-radius: 8px;
  color: var(--espiral-text, #fff);
  background: var(--espiral-surface-background, #151612);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.45);
  font-size: 13px;
  font-weight: 700;
  transform: translateX(-50%);
}

.espiral-copy-toast i {
  color: var(--espiral-accent, #d5da00);
  font-size: 16px;
}

@media (max-width: 767px) {
  .espiral-app-container {
    padding: 14px 12px 28px;
  }

  .espiral-header {
    padding: 14px;
  }

  .espiral-header h2 {
    font-size: 16px;
  }

  .espiral-header p {
    font-size: 12px;
  }

  .espiral-progress {
    gap: 5px;
    font-size: 9px;
  }

  .espiral-progress>i {
    width: 15px;
  }

  .espiral-operation-picker {
    padding-top: 4px;
  }

  .espiral-operation-picker h3 {
    font-size: 21px;
  }

  .espiral-operation-grid {
    grid-template-columns: 1fr;
  }

  .espiral-operation-card {
    min-height: 92px;
    padding: 15px;
  }

  .espiral-wallets-grid,
  .espiral-methods-grid {
    grid-template-columns: 1fr;
  }

  .espiral-form-wrap {
    max-width: 100%;
  }

  .espiral-form {
    padding: 16px;
  }

  .espiral-instructions-modal {
    padding: 22px 16px;
  }
}
</style>
